"use client";

import * as React from "react";

type ToastMessage = {
  id: string;
  title?: string;
  description?: string;
  // add all toast variants here
  variant?: "default" | "destructive" | "success" | "info" | "warning";
  duration?: number;
};

const ToastContext = React.createContext<{
  toasts: ToastMessage[];
  toast: (t: Omit<ToastMessage, "id">) => void;
}>({
  toasts: [],
  toast: () => {},
});

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = React.useState<ToastMessage[]>([]);

  const toast = React.useCallback((t: Omit<ToastMessage, "id">) => {
    const id = Math.random().toString(36).substring(2, 9);
    const duration = t.duration ?? 4000;

    setToasts((current) => [...current, { ...t, id }]);

    const timeout = setTimeout(() => {
      setToasts((current) => current.filter((x) => x.id !== id));
    }, duration);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, toast }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return React.useContext(ToastContext);
}
